import { type YDatabase } from '@databases/collab/y-database';
import { type ValueSdkContract } from '@databases/sdk-types';
import {
  DatabaseFieldType,
  DEFAULT_ENTRY_ID,
  USER_VALUE_CURRENT,
} from '@databases/types/database';
import { ok, type Result } from 'neverthrow';
import { v4 as uuidv4 } from 'uuid';

import { strictDatabaseValueValidator } from '../domain/entries/validators';
import { assertEntryExists } from '../domain/guards/assert-entry-exists';
import { assertFieldExists } from '../domain/guards/assert-field-exists';
import { parse, tryMutate } from '../domain/result';
import {
  clearValueSchema,
  setValueInputSchema,
  type ClearValueInput,
  type SetValueInput,
} from '../domain/values/schemas';
import { clearValue } from '../handlers/value/clear-value';
import { setValue } from '../handlers/value/set-value';
import { type SdkError } from '../types';

/**
 * Public API for reading and mutating database cell values.
 *
 * Every mutation runs a three-step pipeline:
 *   1. **Zod validation** — rejects malformed input → `VALIDATION_ERROR`
 *   2. **Business rules** — rejects invalid document state → `STATE_ERROR`
 *      (ENTRY_NOT_FOUND, FIELD_NOT_FOUND)
 *   3. **Handler mutation** — delegates to collab; only truly unexpected
 *      throws surface as `INTERNAL_ERROR`
 *
 * Values are **not** gated by `isLocked` — cell edits stay allowed
 * when the database structure is locked.
 */
export class ValueSdk implements ValueSdkContract {
  constructor(private readonly ydb: YDatabase) {}

  set(input: SetValueInput): Result<void, SdkError> {
    return parse(setValueInputSchema, input).andThen(
      ({ entryId, fieldId, value, resetValue }) =>
        parse(strictDatabaseValueValidator, generateValueItemIds(value)).andThen(
          (normalizedValue) =>
            this.resolveEntry(entryId)
              .andThen((entry) =>
                assertFieldExists(this.ydb, fieldId).map((field) => ({
                  entry,
                  field,
                }))
              )
              .andThen(({ entry, field }) =>
                tryMutate(() =>
                  setValue(this.ydb, {
                    entry,
                    fieldId,
                    fieldType: field.type,
                    value: normalizedValue,
                    resetValue,
                  })
                )
              )
        )
    );
  }

  clear(input: ClearValueInput): Result<void, SdkError> {
    return parse(clearValueSchema, input).andThen(({ entryId, fieldId }) =>
      this.resolveEntry(entryId)
        .andThen((entry) =>
          assertFieldExists(this.ydb, fieldId).map((field) => ({
            entry,
            field,
          }))
        )
        .andThen(({ entry, field }) =>
          tryMutate(() =>
            clearValue(this.ydb, {
              entry,
              fieldId,
              fieldType: field.type,
            })
          )
        )
    );
  }

  private resolveEntry(entryId: string) {
    return entryId === DEFAULT_ENTRY_ID
      ? ok(this.ydb.defaultEntry)
      : assertEntryExists(this.ydb, entryId);
  }
}

function generateValueItemIds(value: unknown): unknown {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('type' in value) ||
    !('value' in value)
  ) {
    return value;
  }

  switch (value.type) {
    case DatabaseFieldType.HYPERLINK:
    case DatabaseFieldType.PAGE_LINK:
    case DatabaseFieldType.JIRA_ISSUE:
    case DatabaseFieldType.USER:
      if (value.value === USER_VALUE_CURRENT || !Array.isArray(value.value)) {
        return value;
      }

      return {
        ...value,
        value: value.value.map(addGeneratedItemId),
      };

    default:
      return value;
  }
}

function addGeneratedItemId(item: unknown): unknown {
  if (typeof item !== 'object' || item === null) {
    return item;
  }

  if ('_id' in item && typeof item._id === 'string') {
    return item;
  }

  return { ...item, _id: uuidv4() };
}
